import React, { useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyHpDeviceFormData,
    mapHpDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/hpSpec"
import { HpDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_AMENDMENT_CONFIG } from "../../../swrConfig"
import { NewHpDeviceAmendmentResponse } from "../../../types/devices/lct/publishHpDeviceTypes"
import { START_NEW_LCT_DEVICE_AMENDMENT } from "../../../urls/private"

interface IUseStartNewHpDeviceAmendmentOutput {
    data: HpDeviceFormData
    setData: React.Dispatch<React.SetStateAction<HpDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    error?: AppError
}

const startNewHpDeviceAmendmentMapper = getTsIoMapper(
    NewHpDeviceAmendmentResponse,
    "startNewHpDeviceAmendmentMapper"
)

/**
 * Hook for starting a new HP device amendment.
 * Returns data for the device to amend, which is then used to populate the device context
 */
const useStartNewHpDeviceAmendment = (
    deviceRef: string,
    attachmentUUID: string
): IUseStartNewHpDeviceAmendmentOutput => {
    const [data, setData] = useState<HpDeviceFormData>(
        getEmptyHpDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_AMENDMENT(
            deviceRef,
            attachmentUUID,
            LCTOnlyDatasets.HP
        ),
        async (url: string): Promise<void> => {
            try {
                setIsLoading(true)
                const response = await getClientProtected(url)
                const mappedRes = startNewHpDeviceAmendmentMapper.map(response)
                setData(mapHpDeviceResponseToDeviceFormData(mappedRes))
            } catch (err) {
                throw invalidApiDataError(
                    "Failed to start new HP amendment - data load failed"
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_AMENDMENT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewHpDeviceAmendment
